import * as stylex from "@stylexjs/stylex";
import {
  colors,
  fonts,
  fontSizes,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import { type ReactNode } from "react";

import { StoreSection } from "../store-section";
import { firstSentence } from "./store-description";

type StorePlayBeat = { readonly title: string; readonly body: string };
export function StoreBelowFold({
  beats,
  diagramSlot,
  beatsSlot,
  rulesSlot,
  requirementsSlot,
}: {
  readonly beats: readonly StorePlayBeat[];
  readonly diagramSlot: ReactNode;
  readonly beatsSlot?: ReactNode;
  readonly rulesSlot?: ReactNode;
  readonly requirementsSlot?: ReactNode;
}) {
  return (
    <>
      <StoreSection title="How you play">
        <div {...stylex.props(styles.play)}>
          {diagramSlot}
          {beatsSlot ?? (
            <ol {...stylex.props(styles.beats)}>
              {beats.map((beat, index) => (
                <li key={beat.title} {...stylex.props(styles.beat)}>
                  <span {...stylex.props(styles.beatNumber)}>
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div {...stylex.props(styles.beatText)}>
                    <p {...stylex.props(styles.beatTitle)}>{beat.title}</p>
                    <p {...stylex.props(styles.body)}>
                      {firstSentence(beat.body)}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </StoreSection>
      {rulesSlot ? (
        <StoreSection title="Rules & scoring">{rulesSlot}</StoreSection>
      ) : null}
      {requirementsSlot ? (
        <StoreSection title="What you need">{requirementsSlot}</StoreSection>
      ) : null}
    </>
  );
}

const styles = stylex.create({
  play: { display: "flex", flexDirection: "column", gap: spacing.space32 },
  beats: {
    display: "flex",
    minWidth: 0,
    flexDirection: "column",
    gap: spacing.space16,
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  beat: { display: "flex", alignItems: "flex-start", gap: spacing.space16 },
  beatNumber: {
    width: 20,
    flexShrink: 0,
    fontFamily: fonts.mono,
    fontSize: fontSizes.caption,
    lineHeight: "18px",
    color: colors.muted,
  },
  beatText: {
    display: "flex",
    minWidth: 0,
    flexDirection: "column",
    gap: spacing.space4,
  },
  beatTitle: {
    margin: 0,
    fontFamily: fonts.mono,
    fontSize: fontSizes.body,
    fontWeight: 600,
    lineHeight: 1.3,
    color: colors.foreground,
  },
  body: {
    margin: 0,
    fontFamily: fonts.sans,
    fontSize: fontSizes.label,
    lineHeight: 1.5,
    color: colors.secondary,
  },
});
